import type { View, ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import { haptic } from '../haptics';
import { fetchPackManifest, loadPackIndex } from '../../core/region';
import { studyCodes } from '../../core/ignored';
import type { RegionManifest } from '../../types';
import { getXp, masteryBySkill, streakStatus } from '../../core/progress';
import { retryMisses, setRetryMisses } from '../../core/quiz/settings';
import {
  downloadPack,
  packSize,
  packStatus,
  storageInfo,
  packBaseUrl,
  type PackStatus,
  type StorageInfo,
} from '../../core/media/pack';
import { backendLabel, exportSnapshot, importSnapshot } from '../../core/db';
import { currentSession, signOut } from '../../core/storage';
import { snapshotFromJson, snapshotToJson } from '../../core/storage/backup';
import { icon, type IconName } from '../components/icons';
import { getThemePref, setThemePref, type ThemePref } from '../theme';
import { getHaptics, getStripView, setHaptics, setStripView, type StripView } from '../prefs';
import { rampGradient } from '../components/spectrogram-image';
import { t } from '../../strings';
import { megabytes } from '../../strings/format';

ensureStyle(
  'you-css',
  `
  .you { padding:18px var(--pad); display:flex; flex-direction:column; gap:20px; }
  .you h1 { font-size:30px; margin:0; }
  .you h2 { font-size:20px; margin:0; }
  .stats { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
  /* Tappable: three numbers with no room for the sentence that explains them,
     so each one is a way through to the Stats screen that does explain them. */
  .stat { padding:14px 8px; text-align:center; cursor:pointer; font:inherit;
    color:var(--ink); }
  .stat .n { font-family:var(--font-display); font-variation-settings:var(--fvs-display);
    font-size:26px; line-height:1.05; }
  .stat .l { margin-top:4px; display:block; }
  .statsmore { align-self:flex-start; }
  .pack { display:flex; flex-direction:column; gap:14px; }
  /* One pack: what it is, what it would cost, how much is already here, and the
     button. The row had no rules of its own until now, so it laid out as bare
     blocks — the name and the count ran together into "Pacific Northwest140
     birds", and the meter and the button sat welded to whatever was above them. */
  .pack .packrow { display:flex; flex-direction:column; gap:9px; }
  /* Two packs in one panel need a line between them, or the second pack's title
     reads as a fourth line of the first pack's paragraph. */
  .pack .packrow + .packrow { padding-top:14px;
    border-top:1.5px solid var(--panel-line); }
  .pack .head { display:flex; align-items:baseline; gap:8px; flex-wrap:wrap; }
  .pack .head .name { font-family:var(--font-display); font-weight:560;
    font-size:16.5px; line-height:1.2; }
  .pack .desc { line-height:1.5; margin:0; }
  /* The button takes its own width from its label — the two packs' labels are
     different lengths and stretching both to the panel would make that a
     column of mismatched pills. */
  .pack .packrow > .btn { align-self:flex-start; }
  /* What downloading does, said once for both packs — so it sits with the
     heading rather than reading as the first pack's own third sentence. */
  .pack .storage { margin-top:-5px; }
  .pack .warn { color:var(--on-panel-soft); }
  .account { display:flex; flex-direction:column; gap:10px; }
  /* A grid, not a wrapping flex row. At btn-sm's uppercase metrics the three
     labels come to about one gap more than a phone is wide, so flex laid them
     out as two-and-a-stray: "Sign out" alone on a second line with half a row
     of empty space beside it. Backup and Restore are a pair and share a row at
     equal width; Sign out is a different kind of action and takes the row
     beneath it, full width. */
  .account .row { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .account .row > .out { grid-column:1 / -1; }
  .account .msg { font-size:12px; line-height:1.5; color:var(--ink-soft); }
  .account .msg.bad { color:var(--danger); }
  .attrib { display:flex; flex-direction:column; gap:12px; align-items:flex-start; }
  /* Both pickers show swatches rather than a switch, because the whole point
     is choosing between two looks — show them. */
  .theme { display:flex; flex-direction:column; gap:10px; }
  .pickrow { display:grid; gap:8px; }
  .themerow { grid-template-columns:repeat(3,1fr); }
  .striprow { grid-template-columns:repeat(2,1fr); }
  .pickrow button { flex-direction:column; gap:7px; padding:13px 6px; font-size:0.8em;
    font-weight:560; letter-spacing:.1em; text-transform:uppercase; }
  .pickrow button.on { border-color:var(--accent); background:var(--sunk); }
  .pickrow button.on .icon { color:var(--accent); }
  .themerow .swatch { display:flex; gap:3px; }
  .themerow .swatch i { width:11px; height:11px; border-radius:50%;
    border:1px solid var(--hairline-strong); }
  /* The spectrogram's ramp, shown as itself: quiet at the left, the loudest
     cell at the right. The waveform's swatch is the bars it draws. */
  .striprow .ramp { width:44px; height:13px; border-radius:3px;
    border:1px solid var(--hairline-strong); }
  .striprow .bars { display:flex; align-items:center; gap:2px; height:13px; }
  .striprow .bars i { width:3px; border-radius:1px; background:var(--lichen); }
`,
);

export const youView: View = async (root: HTMLElement, ctx: ViewContext) => {
  const [streak, xp, mastery, storage, available, retryOn] = await Promise.all([
    streakStatus(),
    getXp(),
    masteryBySkill(studyCodes()),
    storageInfo(),
    loadPackIndex(),
    retryMisses(),
  ]);
  // Every pack the app ships, not only the selected one — see .packrow above.
  // The index and the manifests are all precached with the shell, so this is a
  // handful of same-origin reads and works offline.
  const manifests = (
    await Promise.all(available.map((p) => fetchPackManifest(p.id).catch(() => null)))
  ).filter((m): m is RegionManifest => m !== null);

  const toStats = () => ctx.navigate('/stats');
  const stats = el(
    'div.stats',
    {},
    stat(
      String(streak.days),
      t('you.stats.streak'),
      streak.atRisk ? t('you.stats.streakWhyAtRisk') : t('you.stats.streakWhy'),
      toStats,
    ),
    stat(String(xp), t('common.totalXp'), t('you.stats.xpWhy'), toStats),
    stat(
      t('common.percent', { percent: mastery.overall }),
      t('common.mastery.label'),
      t('you.stats.masteryWhy'),
      toStats,
    ),
  );

  // ---- Media pack section ----
  const packSection = el(
    'div.pack.panel',
    {},
    el('h2', {}, t('you.pack.title')),
    el('div.storage.meta', {}, storageNote(storage)),
    ...(await Promise.all(manifests.map((m) => packRow(m)))),
    !packBaseUrl(manifests[0]) && el('div.warn.note', {}, t('you.pack.noHost')),
  );

  root.append(
    el(
      'div.you',
      {},
      el('h1', {}, t('common.nav.you')),
      stats,
      el(
        'button.statsmore.btn.btn-quiet.btn-sm',
        { onclick: toStats },
        icon('chart', { size: 16 }),
        t('you.stats.more'),
      ),
      packSection,
      themeSection(),
      stripSection(),
      hapticSection(),
      retrySection(retryOn),
      accountSection(),
      attributionNote(ctx),
    ),
  );
};

/**
 * Light/dark/system, shown as three swatch-bearing buttons. The preference is a
 * device setting in localStorage, deliberately not synced progress — see
 * src/app/theme.ts for why.
 */
function themeSection(): HTMLElement {
  const OPTIONS: { pref: ThemePref; ico: IconName; dots: string[] }[] = [
    { pref: 'hearth', ico: 'sun', dots: ['#f4ece0', '#5c6b45', '#d19a3c'] },
    { pref: 'moss', ico: 'moon', dots: ['#1a1e16', '#7d9159', '#d4a838'] },
    { pref: 'auto', ico: 'monitor', dots: ['#f4ece0', '#1a1e16'] },
  ];

  const row = el('div.pickrow.themerow', {});
  const buttons = OPTIONS.map((o) => {
    const swatch = el(
      'span.swatch',
      {},
      ...o.dots.map((c) => el('i', { style: { background: c } })),
    );
    const b = el(
      'button.btn.btn-quiet',
      { onclick: () => pick(o.pref) },
      icon(o.ico, { size: 19 }),
      swatch,
      t(`you.theme.${o.pref}`),
    );
    row.append(b);
    return b;
  });

  const sync = () => {
    const cur = getThemePref();
    buttons.forEach((b, i) => b.classList.toggle('on', OPTIONS[i].pref === cur));
  };

  const pick = (pref: ThemePref) => {
    setThemePref(pref);
    sync();
  };

  sync();
  return el(
    'div.theme.card',
    {},
    el('h2', {}, t('you.theme.title')),
    el('div.meta', {}, t('you.theme.blurb')),
    row,
  );
}

/**
 * Waveform or spectrogram, for every sound strip in the app.
 *
 * This lives here rather than as a control on the strip itself for two
 * reasons: the strip is one big play button, and a toggle inside it would sit
 * under the thumb that is trying to hear the bird; and the choice is not
 * per-recording — you learn on one axis or the other, and changing it clip by
 * clip is how you end up comparing two pictures that are not the same picture.
 */
function stripSection(): HTMLElement {
  const OPTIONS: { view: StripView; label: string; ico: IconName }[] = [
    { view: 'waveform', label: t('you.strip.waveform'), ico: 'waveform' },
    { view: 'spectrogram', label: t('you.strip.spectrogram'), ico: 'spectrogram' },
  ];

  const row = el('div.pickrow.striprow', {});
  const buttons = OPTIONS.map((o) => {
    const swatch =
      o.view === 'spectrogram'
        ? el('span.ramp', { style: { background: rampGradient() } })
        : el(
            'span.bars',
            {},
            ...[0.35, 0.75, 0.5, 1, 0.4].map((v) =>
              el('i', { style: { height: `${Math.round(v * 13)}px` } }),
            ),
          );
    const b = el(
      'button.btn.btn-quiet',
      { onclick: () => pick(o.view) },
      icon(o.ico, { size: 19 }),
      swatch,
      o.label,
    );
    row.append(b);
    return b;
  });

  const sync = () => {
    const cur = getStripView();
    buttons.forEach((b, i) => b.classList.toggle('on', OPTIONS[i].view === cur));
  };

  const pick = (view: StripView) => {
    setStripView(view);
    sync();
  };

  sync();
  return el(
    'div.theme.card',
    {},
    el('h2', {}, t('you.strip.title')),
    el('div.meta', {}, t('you.strip.blurb')),
    row,
    el('div.note', {}, t('you.strip.note')),
  );
}

/**
 * Whether answering buzzes the phone.
 *
 * Here rather than nowhere because the buzz is the one piece of feedback the
 * app gives that a neighbour can hear, and because it is the only setting whose
 * right answer changes with the room. It is also the one that can be honoured
 * on some devices and not others, so the note says so outright rather than
 * leaving a dead switch to be puzzled over — see src/app/haptics.ts.
 */
function hapticSection(): HTMLElement {
  const OPTIONS: { on: boolean; label: string; ico: IconName }[] = [
    { on: true, label: t('you.haptics.on'), ico: 'waveform' },
    { on: false, label: t('you.haptics.off'), ico: 'volume' },
  ];

  const row = el('div.pickrow.themerow', {});
  const buttons = OPTIONS.map((o) => {
    const b = el(
      'button.btn.btn-quiet',
      { onclick: () => pick(o.on) },
      icon(o.ico, { size: 19 }),
      o.label,
    );
    row.append(b);
    return b;
  });

  const sync = () => {
    const cur = getHaptics();
    buttons.forEach((b, i) => b.classList.toggle('on', OPTIONS[i].on === cur));
  };

  const pick = (on: boolean) => {
    setHaptics(on);
    sync();
    // Answer the tap with the thing being switched on, which is both the
    // confirmation and the only honest preview of it.
    if (on) haptic('right');
  };

  sync();
  return el(
    'div.theme.card',
    {},
    el('h2', {}, t('you.haptics.title')),
    el('div.meta', {}, t('you.haptics.blurb')),
    row,
    el('div.note', {}, t('you.haptics.note')),
  );
}

/**
 * Whether a missed question comes back until it is answered right.
 *
 * Unlike the two pickers above it this one belongs to the learner, not the
 * device — it is a way of studying — so it is stored with the progress and
 * follows the account; see `retryMisses` in core/quiz/settings.ts.
 */
function retrySection(initial: boolean): HTMLElement {
  const OPTIONS: { on: boolean; label: string; ico: IconName }[] = [
    { on: false, label: t('you.retry.once'), ico: 'arrow-right' },
    { on: true, label: t('you.retry.untilRight'), ico: 'undo' },
  ];
  let cur = initial;

  const row = el('div.pickrow.striprow', {});
  const buttons = OPTIONS.map((o) => {
    const b = el(
      'button.btn.btn-quiet',
      { onclick: () => pick(o.on) },
      icon(o.ico, { size: 19 }),
      o.label,
    );
    row.append(b);
    return b;
  });

  const sync = () => {
    buttons.forEach((b, i) => b.classList.toggle('on', OPTIONS[i].on === cur));
  };

  const pick = (on: boolean) => {
    cur = on;
    sync();
    void setRetryMisses(on);
  };

  sync();
  return el(
    'div.theme.card',
    {},
    el('h2', {}, t('you.retry.title')),
    el('div.meta', {}, t('you.retry.blurb')),
    row,
    el('div.note', {}, t('you.retry.note')),
  );
}

/**
 * Where progress is stored, plus the JSON backup pair — the supported way to
 * carry progress between backends, e.g. lifting a local dev database into a
 * PocketBase account.
 */
function accountSection(): HTMLElement {
  const session = currentSession();
  const msg = el('div.msg', {});

  const setMsg = (text: string, bad = false) => {
    msg.textContent = text;
    msg.classList.toggle('bad', bad);
  };

  const backup = el(
    'button.btn.btn-quiet.btn-sm',
    {},
    icon('file-down', { size: 16 }),
    t('you.progress.backup'),
  );
  backup.addEventListener('click', async () => {
    setMsg(t('you.progress.preparing'));
    try {
      const json = snapshotToJson(await exportSnapshot());
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' }),
      );
      const a = el('a', {
        href: url,
        download: `syrinx-progress-${json.exportedAt.slice(0, 10)}.json`,
      }) as HTMLAnchorElement;
      a.click();
      URL.revokeObjectURL(url);
      setMsg(
        t('you.progress.backedUp', { cards: json.cards.length, reviews: json.reviews.length }),
      );
    } catch (err) {
      setMsg((err as Error).message, true);
    }
  });

  const file = el('input', { type: 'file', accept: 'application/json' }) as HTMLInputElement;
  file.style.display = 'none';
  file.addEventListener('change', async () => {
    const picked = file.files?.[0];
    file.value = '';
    if (!picked) return;
    try {
      const snap = snapshotFromJson(JSON.parse(await picked.text()));
      const counts = { cards: snap.cards.length, reviews: snap.reviews.length };
      if (
        !confirm(
          session
            ? t('you.progress.confirmAccount', { email: session.email, ...counts })
            : t('you.progress.confirmDevice', counts),
        )
      ) {
        return;
      }
      setMsg(t('you.progress.restoring'));
      await importSnapshot(snap);
      setMsg(t('you.progress.restored'));
      location.reload();
    } catch (err) {
      setMsg((err as Error).message, true);
    }
  });

  const restore = el(
    'button.btn.btn-quiet.btn-sm',
    { onclick: () => file.click() },
    icon('file-up', { size: 16 }),
    t('you.progress.restore'),
  );

  return el(
    'div.account.card',
    {},
    el('h2', {}, t('you.progress.title')),
    el(
      'div.who.meta',
      {},
      session
        ? t('you.progress.signedIn', { email: session.email })
        : t('you.progress.local', { backend: backendLabel() }),
    ),
    el(
      'div.row',
      {},
      backup,
      restore,
      session &&
        el(
          'button.out.btn.btn-primary.btn-sm',
          { onclick: signOut },
          icon('log-out', { size: 16 }),
          t('you.progress.signOut'),
        ),
    ),
    file,
    msg,
  );
}

/**
 * A pack, its size, and the button that fetches it.
 *
 * Downloading is deliberately separate from selecting: the Home dropdown is not
 * where someone should spend 30 MB of mobile data by accident, and a pack can be
 * usefully fetched long before it is switched to. Until the media is here, that
 * pack's questions fall back to synthetic tones and "photo not downloaded",
 * which are the same paths a half-downloaded pack has always used.
 */
async function packRow(m: RegionManifest): Promise<HTMLElement> {
  const pack = await packStatus(m);
  const size = packSize(m);
  const base = packBaseUrl(m);

  const bar = el('i', { style: { width: `${Math.round(pack.fraction * 100)}%` } });
  const status = el('div.desc.meta', {}, describePack(pack, size, base));
  const done = pack.fraction >= 1;
  const btn = el(
    'button.btn.btn-accent.btn-sm',
    {},
    icon(done ? 'check' : 'download', { size: 16 }),
    // The size goes on the button, not just in the prose: this is the moment
    // someone on a phone decides whether to spend the data.
    done
      ? t('you.pack.downloaded')
      : pack.have
        ? t('you.pack.downloadRemaining', { mb: megabytes(pack.remainingBytes) })
        : t('you.pack.download', { mb: megabytes(pack.remainingBytes) }),
  ) as HTMLButtonElement;
  btn.disabled = done || !base;

  /** Button label + leading icon in one go — the label changes six times below. */
  const setBtn = (text: string, ico: Parameters<typeof icon>[0] = 'download') =>
    btn.replaceChildren(icon(ico, { size: 16 }), text);

  btn.addEventListener('click', async () => {
    btn.disabled = true;
    setBtn(t('you.pack.downloading'));
    let missing = 0;
    try {
      await downloadPack(m, (p) => {
        missing = p.failed;
        // The counter is the run's — the files being fetched now — and the
        // meter is the pack's, which is what it has read as since it was drawn
        // from `pack.fraction`. A resume that reset the bar to zero and climbed
        // back to full would be telling the same lie the counter used to.
        const here = pack.have + p.done;
        bar.style.width = `${Math.round((here / pack.total) * 100)}%`;
        setBtn(t('you.pack.progress', { done: p.done, total: p.total, mb: megabytes(p.bytes) }));
      });
      if (missing > 0) {
        // Almost always "the pack was never uploaded to the host" — say so
        // rather than showing a tick over a pack that isn't there.
        setBtn(t('you.pack.unavailable', { missing }), 'alert');
        btn.disabled = false;
        status.textContent = t('you.pack.hostMissing', { missing });
      } else {
        setBtn(t('you.pack.downloaded'), 'check');
      }
    } catch {
      setBtn(t('you.pack.retry'));
      btn.disabled = false;
    }
  });

  return el(
    'div.packrow',
    {},
    el(
      'div.head',
      {},
      el('span.name', {}, m.label),
      el('span.meta', {}, t('common.birds', { n: m.speciesCount })),
    ),
    status,
    el('div.prog.meter', {}, bar),
    btn,
  );
}

/**
 * One pack in one line: how big it is, and how much of it is here.
 *
 * It used to run to three sentences per pack, and with two packs in the panel
 * that was six lines of prose around two buttons — most of it the same sentence
 * twice, since what downloading MEANS is a fact about the app rather than about
 * the Pacific Northwest. That half now sits once under the heading (see
 * `storageNote`), and this says only what differs between the two.
 */
function describePack(
  pack: PackStatus,
  size: { bytes: number; files: number },
  base: string,
): string {
  if (!base) return t('you.pack.demo');
  const head = { mb: megabytes(size.bytes), files: size.files };
  if (pack.fraction >= 1) return t('you.pack.sizeComplete', head);
  // "8 cached", not "8 of 1199 cached" — the total is the first half of the
  // same line, and repeating it is what pushed this onto two.
  return pack.have
    ? t('you.pack.sizeCached', { ...head, have: pack.have })
    : t('you.pack.sizeNoneCached', head);
}

/**
 * What downloading a pack does, said once for the panel.
 *
 * The storage word is the part worth keeping: "best-effort" means the browser
 * may evict the pack under pressure, which is the one thing a learner who is
 * about to spend 27 MB on a phone should know before they tap.
 */
function storageNote(storage: StorageInfo): string {
  const kind = storage.persisted ? 'persistent' : 'bestEffort';
  return storage.usage
    ? t('you.pack.storageUsed', { kind, mb: megabytes(storage.usage) })
    : t('you.pack.storage', { kind });
}

function attributionNote(ctx: ViewContext): HTMLElement {
  return el(
    'div.attrib.card',
    {},
    el('div.note', {}, t('you.attribution.note')),
    el(
      'button.btn.btn-quiet.btn-sm',
      { onclick: () => ctx.navigate('/credits') },
      icon('book', { size: 16 }),
      t('you.attribution.credits'),
    ),
  );
}

function stat(n: string, l: string, why: string, onclick: () => void): HTMLElement {
  return el(
    'button.stat.card',
    { onclick, title: t('you.stats.tapHint', { why }) },
    el('div.n', {}, n),
    el('div.l.label', {}, l),
  );
}
